/**
 * Lightweight transfer animation on top of the instanced equipment shell.
 * Renders only shutters, FOUPs, and flying carriers — never the full cabinet tree.
 */
import React, { useMemo } from 'react';
import type { LayoutElement } from '../../types';
import { useAppSelector } from '../../hooks/redux';
import { equipmentInstanceToLayoutElement } from '../../utils/equipmentGeometry';
import { useEquipmentPortTransfer } from './amhs/Equipment/useEquipmentFoupTransfer';
import {
  getProcessEquipmentPortPoses,
  resolveProcessLoadPortMetrics,
} from './amhs/Equipment/equipmentPortTransfer';
import { dockFractionsAlongFace, portsOf } from './amhs/Equipment/equipmentPortOwnership';
import { PortShutter } from './amhs/Equipment/PortShutter';
import { FOUP } from './amhs/FOUP/FOUP';
import { resolveFoupKindColor, resolveKitFoupScale } from './amhs/types';
import type { EquipmentRenderDescriptor } from './equipmentSilhouettePorts';
import { resolveKitBox } from './equipmentSilhouettePorts';
import { EquipmentKitWorldGroup } from './EquipmentKitWorldGroup';
import type { Vec3 } from './amhs/types';

/** Match `ProcessEquipment` port groups at `[x, 0, depth * 0.3]`. */
function foupPoseInOverlayGroup(position: Vec3, depth: number): Vec3 {
  return [position[0], position[1], position[2] - depth * 0.3];
}

type Props = {
  descriptor: EquipmentRenderDescriptor;
  element: LayoutElement | null;
};

const EquipmentTransferOverlay3D: React.FC<Props> = React.memo(({ descriptor, element }) => {
  const equipmentInstances = useAppSelector((s) => s.equipment.instances);
  const box = resolveKitBox(descriptor);
  const portCount = Math.max(1, Math.min(6, Math.round(descriptor.loadPortCount ?? 2)));
  const ports = element ? portsOf(element, equipmentInstances) : [];
  const portFractions = dockFractionsAlongFace(ports, portCount);

  const fromSim = useEquipmentPortTransfer({
    element,
    width: box.w,
    height: box.h,
    depth: box.d,
    loadPortCount: portCount,
    standaloneLoadPort: portCount === 1 && element?.type === 'stb',
  });

  const poses = useMemo(
    () => getProcessEquipmentPortPoses(box.w, box.h, box.d, portCount, portFractions),
    [box.d, box.h, box.w, portCount, portFractions]
  );
  const portMetrics = useMemo(
    () => resolveProcessLoadPortMetrics(box.w, box.h, box.d, portCount),
    [box.d, box.h, box.w, portCount]
  );

  // Full FabProcessEquipment kits render transfer animation in-process.
  if (descriptor.kind === 'equipment') {
    return null;
  }

  if (!fromSim.isTransferring && !fromSim.foupPresent && !fromSim.flyingFoup) {
    return null;
  }

  const firstPose = poses[0];
  if (!firstPose) return null;

  const bankShutterOpen =
    fromSim.portVisuals.reduce((most, visual) => Math.max(most, visual?.shutterOpen ?? 0), 0) ?? 0;
  const doorW = portMetrics.width * Math.max(1, portCount) * 0.92;
  const doorH = Math.max(firstPose.shutterSize[1], box.h * 0.28);
  const doorD = Math.max(firstPose.shutterSize[2], box.d * 0.03);
  const dockY = box.h * 0.2;
  const bankMouthZ = firstPose.shutter[2] - box.d * 0.3;
  const kitScale = resolveKitFoupScale(box.w, box.h, box.d);
  const foupColor = resolveFoupKindColor(
    fromSim.equipmentStatus === 'RUNNING' ? 'processed' : 'raw'
  );

  return (
    <EquipmentKitWorldGroup descriptor={descriptor}>
      <group position={[0, 0, box.d * 0.3]} name="TransferOverlay">
        <group position={[0, dockY + doorH * 0.5, bankMouthZ + doorD * 0.2]}>
          <PortShutter
            open={bankShutterOpen}
            width={doorW}
            height={doorH}
            depth={doorD}
            openTravel={doorH * 1.04}
            status={fromSim.equipmentStatus}
          />
        </group>

        {fromSim.portVisuals.map((visual, index) => {
          if (visual.foupMode !== 'resting' || !visual.foupPosition) return null;
          return (
            <FOUP
              key={`rest-${index}`}
              scale={kitScale}
              color={foupColor}
              position={foupPoseInOverlayGroup(visual.foupPosition, box.d)}
            />
          );
        })}

        {fromSim.flyingFoup && (
          <FOUP
            scale={fromSim.flyingFoup.scaleHint ?? kitScale}
            color={foupColor}
            position={foupPoseInOverlayGroup(fromSim.flyingFoup.position, box.d)}
          />
        )}
      </group>
    </EquipmentKitWorldGroup>
  );
});

EquipmentTransferOverlay3D.displayName = 'EquipmentTransferOverlay3D';

export function resolveTransferOverlayElement(
  descriptor: EquipmentRenderDescriptor,
  layoutElements: Record<string, LayoutElement>,
  instances: Record<string, { templateId?: string | null } & object>,
  templates: Record<string, object | null | undefined>
): LayoutElement | null {
  const layout = layoutElements[descriptor.id];
  if (layout) return layout;
  const inst = instances[descriptor.id] as Parameters<typeof equipmentInstanceToLayoutElement>[0] | undefined;
  if (!inst) return null;
  const template = inst.templateId ? (templates[inst.templateId] ?? null) : null;
  return equipmentInstanceToLayoutElement(inst, template as Parameters<typeof equipmentInstanceToLayoutElement>[1]);
}

export default EquipmentTransferOverlay3D;
